import {isSpanishLocale} from '@constants/languages';
import i18next from 'i18next';

/**
 * Gets the appropriate multilingual field value based on current webapp language
 * @param {Object} item - The object containing multilingual fields
 * @param {string} fieldName - The base field name (e.g., 'name', 'description', 'award_type')
 * @param {string} fallback - Fallback value if no field is found
 * @returns {string} The localized field value
 */
export const getLocalizedField = (item, fieldName, fallback = '') => {
  if (!item) return fallback;

  const isSpanish = isSpanishLocale(i18next.language);

  if (isSpanish) {
    // For Spanish users: prioritize es_mx, then en, then legacy field
    const result =
      item[`${fieldName}_es_mx`] ||
      item[`${fieldName}_en`] ||
      item[fieldName] ||
      fallback;

    return result;
  } else {
    // For English users: prioritize en, then legacy field
    return item[`${fieldName}_en`] || item[fieldName] || fallback;
  }
};

/**
 * Gets the localized product name for webapp display
 * @param {Object} product - Product object
 * @returns {string} Localized product name
 */
export const getLocalizedProductName = product => {
  return getLocalizedField(product, 'name', '-');
};

/**
 * Gets the localized product description for webapp display
 * @param {Object} product - Product object
 * @returns {string} Localized product description
 */
export const getLocalizedProductDescription = product => {
  return getLocalizedField(product, 'description', '');
};

/**
 * Gets the localized award type for webapp display
 * @param {Object} award - Award object
 * @returns {string} Localized award type
 */
export const getLocalizedAwardType = award => {
  return getLocalizedField(award, 'award_type', '');
};

/**
 * Gets the localized gift name for webapp display
 * @param {Object} gift - Gift object
 * @returns {string} Localized gift name
 */
export const getLocalizedGiftName = gift => {
  return getLocalizedField(gift, 'name', null);
};
